import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastProvider, useToast } from "../components/Toast";
import AdminSidebar from "../components/AdminSidebar";
import { fetchTestimonialsAdmin, updateTestimonial } from "../../services/api";

const EditTestimonialInner = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTestimonialsAdmin()
      .then((r) => {
        const found = (r.data.data || []).find((t) => t.id === Number(id));
        if (found) {
          setForm({ ...found });
        } else {
          toast("Testimonial not found", "error");
          navigate("/admin/testimonials");
        }
      })
      .catch(() => {
        toast("Failed to load testimonial", "error");
        navigate("/admin/testimonials");
      })
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  const handleSubmit = async () => {
    if (
      !form.name.trim() ||
      !form.role.trim() ||
      !form.company.trim() ||
      !form.avatar.trim() ||
      !form.course.trim() ||
      !form.text.trim()
    ) {
      toast("Please fill all required fields", "error");
      return;
    }
    setSaving(true);
    try {
      await updateTestimonial(form.id, form);
      toast("Testimonial updated successfully!", "success");
      setTimeout(() => navigate("/admin/testimonials"), 1000);
    } catch (e) {
      toast(e.response?.data?.message || "Error updating testimonial", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar
          section="testimonials"
          onSectionChange={(id) =>
            navigate("/admin", { state: { section: id } })
          }
        />
        <div className="admin-content">
          <div
            className="admin-section active"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div style={{ color: "var(--text3)", fontSize: 15 }}>
              Loading testimonial...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!form) return null;

  return (
    <div className="admin-layout">
      <AdminSidebar
        section="testimonials"
        onSectionChange={(id) => navigate("/admin", { state: { section: id } })}
      />
      <div className="admin-content">
        <div className="admin-section active">
          <div className="admin-header">
            <div className="admin-title">✏️ Edit Testimonial</div>
            <button
              className="btn-secondary btn-sm"
              onClick={() => navigate("/admin/testimonials")}
            >
              ← Back to Testimonials
            </button>
          </div>

          <div
            className="admin-card"
            style={{ maxWidth: 2000, borderLeft: "3px solid var(--accent)" }}
          >
            <div
              style={{
                fontFamily: "Syne,sans-serif",
                fontWeight: 700,
                fontSize: 15,
                marginBottom: 20,
                color: "var(--accent)",
              }}
            >
              ✏️ Editing: {form.name}
            </div>

            <div className="form-row" style={{ marginBottom: 14 }}>
              <div>
                <label className="form-label">Name *</label>
                <input
                  className="form-input"
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="form-label">Role *</label>
                <input
                  className="form-input"
                  value={form.role}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, role: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="form-row" style={{ marginBottom: 14 }}>
              <div>
                <label className="form-label">Company *</label>
                <input
                  className="form-input"
                  value={form.company}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, company: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="form-label">Avatar Initials *</label>
                <input
                  className="form-input"
                  maxLength={3}
                  value={form.avatar}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, avatar: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="form-row" style={{ marginBottom: 14 }}>
              <div>
                <label className="form-label">Avatar Background Color</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="color"
                    value={form.avatarBg}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, avatarBg: e.target.value }))
                    }
                    style={{
                      width: 40,
                      height: 36,
                      border: "none",
                      cursor: "pointer",
                      borderRadius: 6,
                    }}
                  />
                  <input
                    className="form-input"
                    value={form.avatarBg}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, avatarBg: e.target.value }))
                    }
                  />
                </div>
              </div>
              <div>
                <label className="form-label">Avatar Text Color</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="color"
                    value={form.avatarCo}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, avatarCo: e.target.value }))
                    }
                    style={{
                      width: 40,
                      height: 36,
                      border: "none",
                      cursor: "pointer",
                      borderRadius: 6,
                    }}
                  />
                  <input
                    className="form-input"
                    value={form.avatarCo}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, avatarCo: e.target.value }))
                    }
                  />
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label className="form-label">Course Name *</label>
              <input
                className="form-input"
                value={form.course}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, course: e.target.value }))
                }
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label className="form-label">Testimonial Text *</label>
              <textarea
                className="form-input"
                rows={5}
                value={form.text}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, text: e.target.value }))
                }
                style={{ resize: "vertical" }}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label className="form-label">Status</label>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        isActive: e.target.checked,
                      }))
                    }
                  />
                  <span style={{ fontSize: 13 }}>Active (show on website)</span>
                </label>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label className="form-label">Rating (1–5)</label>
              <select
                className="form-input"
                value={form.rating}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    rating: Number(e.target.value),
                  }))
                }
                style={{ maxWidth: 160 }}
              >
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={n}>
                    {n} ★
                  </option>
                ))}
              </select>
            </div>

            {/* Preview */}
            <div
              style={{
                background: "var(--bg2)",
                borderRadius: 10,
                padding: "14px 16px",
                marginBottom: 20,
                border: "1px solid var(--border)",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: "var(--text3)",
                  marginBottom: 10,
                  fontWeight: 600,
                }}
              >
                PREVIEW
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: form.avatarBg,
                    color: form.avatarCo,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {form.avatar || "?"}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {form.name}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text3)" }}>
                    {form.role} @ {form.company}
                  </div>
                </div>
                <div style={{ marginLeft: "auto", color: "#f59e0b" }}>
                  {"★".repeat(form.rating)}
                </div>
              </div>
              {form.text && (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 13,
                    color: "var(--text2)",
                    fontStyle: "italic",
                  }}
                >
                  "{form.text}"
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={saving}
                style={{ opacity: saving ? 0.7 : 1 }}
              >
                {saving ? "Saving..." : "💾 Update Testimonial"}
              </button>
              <button
                className="btn-secondary"
                onClick={() => navigate("/admin/testimonials")}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EditTestimonialPage = () => (
  <ToastProvider>
    <EditTestimonialInner />
  </ToastProvider>
);

export default EditTestimonialPage;
